const KPI_CUTOFF_DATE = new Date('2022-01-01T00:00:00');
const ENABLE_KPI_CUTOFF_DATE = false;

const normalizeText = (value) => {
    if (!value) {
        return '';
    }

    return value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();
};

const parseFlexibleDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
            return null;
        }

        if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmedValue)) {
            const [datePart] = trimmedValue.split(' ');
            const [day, month, year] = datePart.split('/');
            const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        const parsedIsoDate = new Date(trimmedValue);
        if (!Number.isNaN(parsedIsoDate.getTime())) {
            return parsedIsoDate;
        }
    }

    return null;
};

const getExpirationMonths = (conclusaoTecnica) => {
    const normalized = normalizeText(conclusaoTecnica);

    if (normalized === 'APROVADO 3 MESES') {
        return 3;
    }

    if (normalized === 'APROVADO 6 MESES') {
        return 6;
    }

    if (normalized === 'APROVADO 12 MESES') {
        return 12;
    }

    return 0;
};

const addMonths = (date, months) => {
    const clonedDate = new Date(date);
    clonedDate.setMonth(clonedDate.getMonth() + months);
    return clonedDate;
};

const diffInDays = (startDate, endDate) => {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    return Math.round((endDate.getTime() - startDate.getTime()) / millisecondsPerDay);
};

const countBy = (items, selector) => {
    return items.reduce((accumulator, item) => {
        const key = selector(item);

        if (!key) {
            return accumulator;
        }

        accumulator[key] = (accumulator[key] || 0) + 1;
        return accumulator;
    }, {});
};

const sortCountEntries = (entries) => {
    return Object.entries(entries)
        .map(([label, total]) => ({ label, total }))
        .sort((itemA, itemB) => itemB.total - itemA.total || itemA.label.localeCompare(itemB.label));
};

const getAssetKey = (laudo) => {
    return (
        laudo.tagInguanti ||
        laudo.tagCliente ||
        [laudo.empresa, laudo.acessorio, laudo.setor, laudo.subSetor]
            .filter(Boolean)
            .join(' | ')
    );
};

const getLatestLaudoDate = (laudo) => {
    return (
        parseFlexibleDate(laudo.dataMetodologiaInspecao) ||
        parseFlexibleDate(laudo.dataCadastro) ||
        parseFlexibleDate(laudo.dataAlteracao) ||
        null
    );
};

export const buildDashboardKpis = ({
    laudos = [],
    ordensServico = [],
    includeClientRanking = true,
    includeOrderServiceKpis = true,
    includeValidityKpis = true,
    includeInspectorRanking = true,
    referenceDate = new Date()
}) => {
    const normalizedLaudos = Array.isArray(laudos) ? laudos : [];
    const normalizedOrdens = Array.isArray(ordensServico) ? ordensServico : [];

    const now = parseFlexibleDate(referenceDate) || new Date();

    const aprovados = normalizedLaudos.filter((item) => {
        const conclusao = normalizeText(item.conclusaoTecnica);
        return conclusao === 'APROVADO 3 MESES' || conclusao === 'APROVADO 6 MESES' || conclusao === 'APROVADO 12 MESES';
    });

    const recomendacoes = normalizedLaudos.filter((item) => normalizeText(item.conclusaoTecnica).includes('RECOMENDACOES'));
    const reprovados = normalizedLaudos.filter((item) => normalizeText(item.conclusaoTecnica) === 'REPROVADO');

    const approvalRate = normalizedLaudos.length ? ((aprovados.length / normalizedLaudos.length) * 100).toFixed(1) : '0.0';
    const recommendationRate = normalizedLaudos.length ? ((recomendacoes.length / normalizedLaudos.length) * 100).toFixed(1) : '0.0';
    const reprovalRate = normalizedLaudos.length ? ((reprovados.length / normalizedLaudos.length) * 100).toFixed(1) : '0.0';
    const currentYear = now.getFullYear();
    const last30DaysDate = new Date(now);
    last30DaysDate.setDate(now.getDate() - 30);

    const laudosAnoAtual = normalizedLaudos.filter((item) => {
        const inspectionDate = parseFlexibleDate(item.dataMetodologiaInspecao);
        return inspectionDate && inspectionDate.getFullYear() === currentYear;
    }).length;

    const laudosUltimos30Dias = normalizedLaudos.filter((item) => {
        const inspectionDate = parseFlexibleDate(item.dataMetodologiaInspecao);
        return inspectionDate && inspectionDate >= last30DaysDate;
    }).length;

    const latestLaudoByOs = normalizedLaudos.reduce((accumulator, laudo) => {
        if (!laudo.idOrdemServico) {
            return accumulator;
        }

        const currentDate = getLatestLaudoDate(laudo);
        if (!currentDate) {
            return accumulator;
        }

        const previous = accumulator[laudo.idOrdemServico];
        if (!previous || currentDate > previous) {
            accumulator[laudo.idOrdemServico] = currentDate;
        }

        return accumulator;
    }, {});

    const closeTimes = normalizedOrdens.reduce((accumulator, ordemServico) => {
        const createdAt = parseFlexibleDate(ordemServico.dataCadastro);
        const closedAt = latestLaudoByOs[ordemServico.idOrdemServico];

        if (!createdAt || !closedAt || closedAt < createdAt) {
            return accumulator;
        }

        accumulator.push(diffInDays(createdAt, closedAt));
        return accumulator;
    }, []);

    const averageCloseDays = closeTimes.length
        ? (closeTimes.reduce((sum, current) => sum + current, 0) / closeTimes.length).toFixed(1)
        : '0.0';

    const validatableLaudos = normalizedLaudos
        .map((laudo) => {
            const inspectionDate = parseFlexibleDate(laudo.dataMetodologiaInspecao);
            const expirationMonths = getExpirationMonths(laudo.conclusaoTecnica);

            // Mantido no codigo para reativacao futura, mas atualmente sem aplicar a data de corte.
            if (!inspectionDate || expirationMonths === 0 || (ENABLE_KPI_CUTOFF_DATE && inspectionDate < KPI_CUTOFF_DATE)) {
                return null;
            }

            const expirationDate = addMonths(inspectionDate, expirationMonths);
            const daysUntilExpiration = diffInDays(now, expirationDate);

            return {
                ...laudo,
                inspectionDate,
                expirationDate,
                daysUntilExpiration
            };
        })
        .filter(Boolean);

    const vencidos = validatableLaudos.filter((item) => item.expirationDate < now);
    const vencendo30 = validatableLaudos.filter((item) => item.daysUntilExpiration >= 0 && item.daysUntilExpiration <= 30);
    const vencendo60 = validatableLaudos.filter((item) => item.daysUntilExpiration >= 0 && item.daysUntilExpiration <= 60);
    const vencendo90 = validatableLaudos.filter((item) => item.daysUntilExpiration >= 0 && item.daysUntilExpiration <= 90);
    const validos = validatableLaudos.filter((item) => item.expirationDate >= now);
    const validosRate = validatableLaudos.length ? ((validos.length / validatableLaudos.length) * 100).toFixed(1) : '0.0';

    const laudosPorAtivo = normalizedLaudos.reduce((accumulator, laudo) => {
        const assetKey = getAssetKey(laudo);

        if (!assetKey) {
            return accumulator;
        }

        accumulator[assetKey] = (accumulator[assetKey] || 0) + 1;
        return accumulator;
    }, {});

    const ativosReinspecionados = Object.values(laudosPorAtivo).filter((total) => total > 1).length;
    const totalAtivos = Object.keys(laudosPorAtivo).length;
    const retrabalhoRate = totalAtivos ? ((ativosReinspecionados / totalAtivos) * 100).toFixed(1) : '0.0';
    const finalizationRate = normalizedOrdens.length
        ? ((normalizedOrdens.filter((item) => item.finalizado).length / normalizedOrdens.length) * 100).toFixed(1)
        : '0.0';

    const topClientes = includeClientRanking
        ? sortCountEntries(countBy(normalizedLaudos, (item) => item.empresa || item.cliente)).slice(0, 5)
        : [];

    const topInspetores = sortCountEntries(countBy(normalizedLaudos, (item) => item.nomeInspetor)).slice(0, 5);
    const topSetores = sortCountEntries(countBy(normalizedLaudos, (item) => item.setor)).slice(0, 5);
    const topSubSetoresReprovados = sortCountEntries(countBy(reprovados, (item) => item.subSetor)).slice(0, 5);
    const topAcessoriosReprovados = sortCountEntries(countBy(reprovados, (item) => item.acessorio)).slice(0, 5);

    const cards = [
        ...(includeOrderServiceKpis && normalizedOrdens.length > 0 ? [
            { title: 'O.S. abertas', value: normalizedOrdens.filter((item) => !item.finalizado).length, accent: 'warning' },
            { title: 'O.S. finalizadas', value: normalizedOrdens.filter((item) => item.finalizado).length, accent: 'success' },
            { title: 'Tempo medio fechamento', value: `${averageCloseDays} dias`, accent: 'primary' },
            { title: 'Taxa finalizacao O.S.', value: `${finalizationRate}%`, accent: 'info' }
        ] : []),
        { title: 'Total laudos', value: normalizedLaudos.length, accent: 'primary' },
        { title: 'Laudos no ano', value: laudosAnoAtual, accent: 'secondary' },
        { title: 'Laudos 30 dias', value: laudosUltimos30Dias, accent: 'dark' },
        { title: 'Ativos monitorados', value: totalAtivos, accent: 'info' },
        { title: 'Taxa aprovacao', value: `${approvalRate}%`, accent: 'success' },
        { title: 'Taxa recomendacao', value: `${recommendationRate}%`, accent: 'warning' },
        { title: 'Taxa reprovacao', value: `${reprovalRate}%`, accent: 'danger' },
        { title: 'Ativos reinspecionados', value: ativosReinspecionados, accent: 'secondary' },
        { title: 'Taxa retrabalho', value: `${retrabalhoRate}%`, accent: 'dark' }
    ];

    return {
        cards,
        validityCards: includeValidityKpis ? [
            { title: 'Laudos validos', value: validos.length, accent: 'success' },
            { title: 'Vencidos', value: vencidos.length, accent: 'danger' },
            { title: 'Vencendo em 30 dias', value: vencendo30.length, accent: 'warning' },
            { title: 'Vencendo em 60 dias', value: vencendo60.length, accent: 'info' },
            { title: 'Vencendo em 90 dias', value: vencendo90.length, accent: 'primary' },
            { title: 'Taxa validade ativa', value: `${validosRate}%`, accent: 'secondary' }
        ] : [],
        rankings: [
            includeClientRanking ? { title: 'Top clientes por inspeções', items: topClientes } : null,
            ...(includeInspectorRanking ? [{ title: 'Top inspetores', items: topInspetores }] : []),
            { title: 'Top setores', items: topSetores },
            { title: 'Top sub setores reprovados', items: topSubSetoresReprovados },
            { title: 'Top acessórios reprovados', items: topAcessoriosReprovados }
        ].filter(Boolean),
        metadata: {
            totalLaudos: normalizedLaudos.length,
            totalOrdensServico: normalizedOrdens.length,
            totalLaudosComCorte: validatableLaudos.length,
            cutoffDate: KPI_CUTOFF_DATE
        }
    };
};

export const buildYearlyConclusionComparison = (laudos = []) => {
    const totalsByYear = (Array.isArray(laudos) ? laudos : []).reduce((accumulator, laudo) => {
        const inspectionDate = parseFlexibleDate(laudo.dataMetodologiaInspecao);
        if (!inspectionDate) {
            return accumulator;
        }

        const year = inspectionDate.getFullYear();
        const conclusion = normalizeText(laudo.conclusaoTecnica);
        const isApproved = conclusion === 'APROVADO 3 MESES' || conclusion === 'APROVADO 6 MESES' || conclusion === 'APROVADO 12 MESES';
        const isRecommended = conclusion.includes('RECOMENDACOES');
        const isRejected = conclusion === 'REPROVADO';

        if (!isApproved && !isRecommended && !isRejected) {
            return accumulator;
        }

        if (!accumulator[year]) {
            accumulator[year] = { year, approved: 0, recommended: 0, rejected: 0 };
        }

        if (isApproved) accumulator[year].approved += 1;
        if (isRecommended) accumulator[year].recommended += 1;
        if (isRejected) accumulator[year].rejected += 1;
        return accumulator;
    }, {});

    return Object.values(totalsByYear).sort((first, second) => first.year - second.year);
};

export const formatKpiCutoffDate = () => '01/01/2022';
export const isKpiCutoffEnabled = () => ENABLE_KPI_CUTOFF_DATE;
